import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { api, unwrap } from "../api/client";
import type { components } from "../api/schema";

type Settings = components["schemas"]["AnalyticsSettings"];
type Speech = components["schemas"]["SpeechSettings"];
type Motion = components["schemas"]["MotionSettings"];
type Profile = components["schemas"]["AnalysisProfile"];
type Thresholds = components["schemas"]["UpdateAnalyticsSettings"];

const KEY = ["analytics-settings"] as const;
const PROFILES = ["analysis-profiles"] as const;

const TITLES: Record<string, string> = {
  video: "Video recordings",
  audio: "Audio recordings",
};

/** A numeric field shown in its own unit, stored as `scale` × that. */
function NumberField({
  label,
  value,
  scale = 1,
  helperText,
  onChange,
}: {
  label: string;
  value: number;
  scale?: number;
  helperText?: string;
  onChange: (v: number) => void;
}) {
  return (
    <TextField
      label={label}
      type="number"
      size="small"
      value={Math.round((value / scale) * 1000) / 1000}
      helperText={helperText}
      onChange={(e) => onChange(Number(e.target.value) * scale)}
      sx={{ width: 190 }}
    />
  );
}

/** Analytics thresholds per kind of recording (design §8). */
export function AnalyticsPage() {
  const settings = useQuery({
    queryKey: KEY,
    queryFn: () => unwrap(api.GET("/api/analytics/settings")),
  });
  return (
    <Stack spacing={3} component="section" aria-label="Analytics">
      <Typography variant="h5" component="h2">
        Analytics
      </Typography>
      <Typography color="text.secondary">
        Thresholds for finding speech, loud events and motion, set separately
        for video and audio recordings. New settings apply to recordings
        analysed from now on; run the analysis again to apply them to earlier
        ones. Earlier results are kept as older versions.
      </Typography>
      {settings.isError && (
        <Alert severity="error">{settings.error.message}</Alert>
      )}
      {settings.data?.kinds.map((k) => (
        <KindSettings key={k.media_kind} settings={k} />
      ))}
      {settings.data && <Profiles defaults={settings.data} />}
    </Stack>
  );
}

function KindSettings({ settings }: { settings: Settings }) {
  const qc = useQueryClient();
  const [speech, setSpeech] = useState<Speech>(settings.speech);
  const [motion, setMotion] = useState<Motion>(settings.motion);
  const video = settings.media_kind === "video";
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.PUT("/api/analytics/settings/{media_kind}", {
          params: { path: { media_kind: settings.media_kind } },
          body: { speech, motion },
        }),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
  const rerun = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/analytics/rerun", {
          body: {
            media_kind: settings.media_kind,
            kinds: video ? ["speech", "motion"] : ["speech"],
          },
        }),
      ),
  });
  const title = TITLES[settings.media_kind] ?? settings.media_kind;

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      component="section"
      aria-label={title}
    >
      <Typography variant="h6" component="h3">
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {settings.updated_at
          ? `Changed ${settings.updated_at.slice(0, 16).replace("T", " ")} UTC by ${settings.updated_by ?? "someone"}`
          : "The defaults"}
      </Typography>
      <Stack spacing={2}>
        <ThresholdFields
          video={video}
          value={{ speech, motion }}
          onChange={(v) => {
            setSpeech(v.speech);
            setMotion(v.motion);
          }}
        />
        {save.isError && <Alert severity="error">{save.error.message}</Alert>}
        {rerun.isError && <Alert severity="error">{rerun.error.message}</Alert>}
        {rerun.isSuccess && (
          <Alert severity="success">
            Analysis queued again for {rerun.data.queued} recordings.
          </Alert>
        )}
        <Stack direction="row" sx={{ gap: 2 }}>
          <Button
            variant="contained"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
          <Button
            disabled={rerun.isPending}
            onClick={() => {
              if (
                window.confirm(
                  `Analyse every ${settings.media_kind} recording again with the saved settings? This can take a while; earlier results are kept.`,
                )
              )
                rerun.mutate();
            }}
          >
            Run analysis again
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

function ThresholdFields({
  video,
  value,
  onChange,
}: {
  video: boolean;
  value: Thresholds;
  onChange: (v: Thresholds) => void;
}) {
  const { speech, motion } = value;
  const s = (patch: Partial<Speech>) =>
    onChange({ speech: { ...speech, ...patch }, motion });
  const m = (patch: Partial<Motion>) =>
    onChange({ speech, motion: { ...motion, ...patch } });
  return (
    <>
      <Typography variant="subtitle2">Speech</Typography>
      <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap" }}>
        <NumberField
          label="Starts at (%)"
          value={speech.on}
          scale={0.01}
          helperText="Voice probability"
          onChange={(on) => s({ on })}
        />
        <NumberField
          label="Ends below (%)"
          value={speech.off}
          scale={0.01}
          onChange={(off) => s({ off })}
        />
        <NumberField
          label="Shortest (s)"
          value={speech.min_speech_ms}
          scale={1000}
          onChange={(v) => s({ min_speech_ms: Math.round(v) })}
        />
        <NumberField
          label="Merge gaps under (s)"
          value={speech.merge_gap_ms}
          scale={1000}
          onChange={(v) => s({ merge_gap_ms: Math.round(v) })}
        />
      </Stack>
      <Typography variant="subtitle2">Loud events</Typography>
      <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap" }}>
        <NumberField
          label="Rise (dB)"
          value={speech.loud_rise_db}
          helperText="Above the recent level"
          onChange={(loud_rise_db) => s({ loud_rise_db })}
        />
        <NumberField
          label="At least (dBFS)"
          value={speech.loud_floor_dbfs}
          onChange={(loud_floor_dbfs) => s({ loud_floor_dbfs })}
        />
      </Stack>
      {video && (
        <>
          <Typography variant="subtitle2">Motion</Typography>
          <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap" }}>
            <NumberField
              label="Pixel change (0-255)"
              value={motion.pixel_threshold}
              onChange={(pixel_threshold) => m({ pixel_threshold })}
            />
            <NumberField
              label="Starts at (% of picture)"
              value={motion.on}
              scale={0.01}
              onChange={(on) => m({ on })}
            />
            <NumberField
              label="Ends below (% of picture)"
              value={motion.off}
              scale={0.01}
              onChange={(off) => m({ off })}
            />
            <NumberField
              label="Camera moving at (%)"
              value={motion.global}
              scale={0.01}
              onChange={(global) => m({ global })}
            />
            <NumberField
              label="Shortest (s)"
              value={motion.min_motion_ms}
              scale={1000}
              onChange={(v) => m({ min_motion_ms: Math.round(v) })}
            />
            <NumberField
              label="Merge gaps under (s)"
              value={motion.merge_gap_ms}
              scale={1000}
              onChange={(v) => m({ merge_gap_ms: Math.round(v) })}
            />
          </Stack>
        </>
      )}
    </>
  );
}

/**
 * Named sets of thresholds that libraries use instead of the Default above,
 * e.g. for a team whose recordings are windy or quiet.
 */
function Profiles({
  defaults,
}: {
  defaults: components["schemas"]["AnalyticsSettingsList"];
}) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: PROFILES,
    queryFn: () => unwrap(api.GET("/api/analytics/profiles")),
  });
  const [adding, setAdding] = useState(false);
  const kind = (k: string): Thresholds => {
    const d = defaults.kinds.find((x) => x.media_kind === k);
    return d
      ? { speech: d.speech, motion: d.motion }
      : { speech: defaults.default_speech, motion: defaults.default_motion };
  };
  const assign = useMutation({
    mutationFn: (v: { library_id: string; profile_id: string | null }) =>
      unwrap(api.PUT("/api/analytics/libraries", { body: v })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: PROFILES }),
  });
  const rerun = useMutation({
    mutationFn: (library_id: string) =>
      unwrap(
        api.POST("/api/analytics/rerun", {
          body: { kinds: ["speech", "motion"], library_id },
        }),
      ),
  });
  const profiles = list.data?.profiles ?? [];
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      component="section"
      aria-label="Analysis profiles"
    >
      <Typography variant="h6" component="h3">
        Analysis profiles
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Named thresholds a library can use instead of the Default above, for
        example for a team whose recordings are usually windy. Results record
        which profile they were made with.
      </Typography>
      {list.isError && <Alert severity="error">{list.error.message}</Alert>}
      <Stack spacing={2}>
        {profiles.map((p) => (
          <ProfileForm key={p.id} profile={p} start={p} />
        ))}
        {adding ? (
          <ProfileForm
            start={{ name: "", video: kind("video"), audio: kind("audio") }}
            onDone={() => setAdding(false)}
          />
        ) : (
          <Button sx={{ alignSelf: "start" }} onClick={() => setAdding(true)}>
            New profile
          </Button>
        )}
        {list.data && list.data.libraries.length > 0 && (
          <Table size="small" aria-label="Libraries">
            <TableHead>
              <TableRow>
                <TableCell>Library</TableCell>
                <TableCell>Profile</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.data.libraries.map((l) => (
                <TableRow key={l.library_id}>
                  <TableCell>{l.team}</TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      aria-label={`Profile for ${l.team}`}
                      value={l.profile_id ?? ""}
                      onChange={(e) =>
                        assign.mutate({
                          library_id: l.library_id,
                          profile_id: e.target.value || null,
                        })
                      }
                      sx={{ minWidth: 180 }}
                      slotProps={{ select: { displayEmpty: true } }}
                    >
                      <MenuItem value="">Default</MenuItem>
                      {profiles.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      disabled={rerun.isPending}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Analyse every recording in ${l.team} again? This can take a while; earlier results are kept.`,
                          )
                        )
                          rerun.mutate(l.library_id);
                      }}
                    >
                      Run analysis again
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {assign.isError && (
          <Alert severity="error">{assign.error.message}</Alert>
        )}
        {rerun.isError && <Alert severity="error">{rerun.error.message}</Alert>}
        {rerun.isSuccess && (
          <Alert severity="success">
            Analysis queued again for {rerun.data.queued} recordings.
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}

function ProfileForm({
  profile,
  start,
  onDone,
}: {
  profile?: Profile;
  start: { name: string; video: Thresholds; audio: Thresholds };
  onDone?: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(start.name);
  const [videoT, setVideo] = useState<Thresholds>(start.video);
  const [audioT, setAudio] = useState<Thresholds>(start.audio);
  const done = () => {
    void qc.invalidateQueries({ queryKey: PROFILES });
    onDone?.();
  };
  const save = useMutation({
    mutationFn: () => {
      const body = { name, video: videoT, audio: audioT };
      return profile
        ? unwrap(
            api.PUT("/api/analytics/profiles/{id}", {
              params: { path: { id: profile.id } },
              body,
            }),
          )
        : unwrap(api.POST("/api/analytics/profiles", { body }));
    },
    onSuccess: done,
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.DELETE("/api/analytics/profiles/{id}", {
          params: { path: { id } },
        }),
      ),
    onSuccess: done,
  });
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      component="section"
      aria-label={profile ? `Profile ${profile.name}` : "New profile"}
    >
      <Stack spacing={2}>
        <TextField
          label="Name"
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ maxWidth: 300 }}
        />
        {profile && (
          <Typography variant="body2" color="text.secondary">
            Changed {profile.updated_at.slice(0, 16).replace("T", " ")} UTC by{" "}
            {profile.updated_by ?? "someone"}
          </Typography>
        )}
        <Typography variant="subtitle1">{TITLES.video}</Typography>
        <ThresholdFields video value={videoT} onChange={setVideo} />
        <Typography variant="subtitle1">{TITLES.audio}</Typography>
        <ThresholdFields video={false} value={audioT} onChange={setAudio} />
        {save.isError && <Alert severity="error">{save.error.message}</Alert>}
        {remove.isError && (
          <Alert severity="error">{remove.error.message}</Alert>
        )}
        <Stack direction="row" sx={{ gap: 2 }}>
          <Button
            variant="contained"
            disabled={save.isPending || !name.trim()}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
          {profile ? (
            <Button
              color="error"
              disabled={remove.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    `Delete the profile ${profile.name}? Its libraries go back to the Default.`,
                  )
                )
                  remove.mutate(profile.id);
              }}
            >
              Delete
            </Button>
          ) : (
            <Button onClick={onDone}>Cancel</Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}
